import { AllocationMethodType } from '../types';

export function formatCurrency(
  amount: number,
  currency = 'USD',
  compact = false
): string {
  if (compact && Math.abs(amount) >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(2)}M ${currency}`;
  }
  if (compact && Math.abs(amount) >= 1_000) {
    return `${(amount / 1_000).toFixed(1)}k ${currency}`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatPercent(value: number, includeSign = true): string {
  const formatted = `${value >= 0 && includeSign ? '+' : ''}${value.toFixed(2)}%`;
  return formatted;
}

export function formatNumber(val: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(val);
}

export function formatDate(dateString: string): string {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

export function getAllocationMethodBadgeClass(
  method: AllocationMethodType
): string {
  switch (method) {
    case 'fixed_lot':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800';
    case 'balance_ratio':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
    case 'equity_ratio':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200 dark:border-purple-800';
    case 'percentage':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    case 'multiplier':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800';
    case 'risk_based':
      return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200 dark:border-rose-800';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
}

export function calculateTradeAllocationPreview(
  masterLots: number,
  subAccounts: { id: string; name: string; equity: number; balance: number }[],
  method: AllocationMethodType,
  params: {
    fixedLotValue?: number;
    multiplierFactor?: number;
    riskPercent?: number;
  } = {}
): {
  subAccountId: string;
  subAccountName: string;
  allocatedLots: number;
  percentOfTotal: number;
}[] {
  const totalBalance = subAccounts.reduce((acc, curr) => acc + curr.balance, 0);
  const totalEquity = subAccounts.reduce((acc, curr) => acc + curr.equity, 0);

  return subAccounts.map(account => {
    let lots = 0;
    if (method === 'fixed_lot') {
      lots = params.fixedLotValue || 0.1;
    } else if (method === 'balance_ratio') {
      lots =
        totalBalance > 0
          ? Number(((account.balance / totalBalance) * masterLots).toFixed(2))
          : 0;
    } else if (method === 'equity_ratio') {
      lots =
        totalEquity > 0
          ? Number(((account.equity / totalEquity) * masterLots).toFixed(2))
          : 0;
    } else if (method === 'percentage') {
      lots = Number((masterLots / subAccounts.length).toFixed(2));
    } else if (method === 'multiplier') {
      lots = Number((masterLots * (params.multiplierFactor || 1)).toFixed(2));
    } else if (method === 'risk_based') {
      const riskRatio = (params.riskPercent || 2) / 100;
      lots = Number(((account.equity * riskRatio) / 1000).toFixed(2));
    }

    const percentOfTotal =
      masterLots > 0
        ? Number(((lots / (masterLots || 1)) * 100).toFixed(1))
        : 0;

    return {
      subAccountId: account.id,
      subAccountName: account.name,
      allocatedLots: Math.max(0.01, lots),
      percentOfTotal,
    };
  });
}
